import { useContext } from 'react';
import { Box, Button, Flex, Text, Grid } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { MeContext } from '../../../context/MeContext';
import { useGetOnboardingStateQuery } from '../../../store/redux/api/onboardingApi';
import { useListPassesQuery } from '../../../store/redux/api/passApi';
import type {
  OnboardingNextStep,
  OnboardingStateResponse,
  OnboardingStepKey,
} from '../../../types/onboarding';
import { OnboardingCheckIcon } from '../../../theme/icons/OnboardingCheckIcon';
import { OnboardingArrowIcon } from '../../../theme/icons/OnboardingArrowIcon';
import { useCreateTemplateAction } from '../pass-templates-new/hooks/useCreateTemplateAction';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

type StepConfig = {
  key: OnboardingStepKey;
  nextStepName: Exclude<OnboardingNextStep, 'complete'>;
  title: string;
  blurb: string;
  href: string;
  eta: string;
};

const STEPS: readonly StepConfig[] = [
  {
    key: 'template_created',
    nextStepName: 'create_template',
    title: 'Create a pass template',
    blurb: 'Design how your pass looks in the Apple & Google Wallet.',
    href: '/pass-templates/create',
    eta: '~5 min',
  },
  {
    key: 'pass_created',
    nextStepName: 'create_pass',
    title: 'Create your first pass',
    blurb: 'Generate a test pass from your template.',
    href: '/passes/create',
    eta: '~2 min',
  },
  {
    key: 'pass_added_to_wallet',
    nextStepName: 'add_to_wallet',
    title: 'Add your pass to wallet',
    blurb: 'Scan the QR code or open the install link on your phone.',
    href: '/passes',
    eta: '~1 min',
  },
  {
    key: 'notification_sent',
    nextStepName: 'send_notification',
    title: 'Send a notification',
    blurb: 'Send a push notification and see it on your phone in real time.',
    href: '/passes',
    eta: '~1 min',
  },
];

const wobble = keyframes`
  0%, 100% { transform: translateY(1px); }
  50%      { transform: translateY(-1px); }
`;

type StepStatus = 'done' | 'active' | 'locked';

const statusFor = (
  step: StepConfig,
  data: OnboardingStateResponse
): StepStatus => {
  if (data.steps[step.key]) return 'done';
  if (data.next_step === step.nextStepName) return 'active';
  return 'locked';
};

const StepBadge = ({ status, n }: { status: StepStatus; n: number }) => {
  if (status === 'done') {
    return (
      <Flex
        align="center"
        justify="center"
        boxSize="24px"
        borderRadius="full"
        bg="orangePrimary"
        opacity={0.7}
        color="white"
        flex="0 0 auto"
      >
        <OnboardingCheckIcon boxSize="12px" />
      </Flex>
    );
  }
  if (status === 'active') {
    return (
      <Flex
        align="center"
        justify="center"
        boxSize="24px"
        borderRadius="full"
        bg="orangePrimary"
        color="white"
        fontFamily="heading"
        fontWeight="800"
        fontSize="12px"
        flex="0 0 auto"
      >
        {n}
      </Flex>
    );
  }
  return (
    <Flex
      align="center"
      justify="center"
      boxSize="24px"
      fontSize="12px"
      borderRadius="full"
      bg="blackAlpha.200"
      border="1px dashed"
      borderColor="blackAlpha.600"
      color="blackAlpha.700"
      flex="0 0 auto"
    >
      {n}
    </Flex>
  );
};

const StepCard = ({
  step,
  index,
  status,
  onStart,
}: {
  step: StepConfig;
  index: number;
  status: StepStatus;
  onStart: () => void;
}) => {
  const isActive = status === 'active';
  const isDone = status === 'done';

  return (
    <Flex
      flex="1 1 0"
      minW={0}
      direction="column"
      gap={3.5}
      position="relative"
      px="14px"
      pt="13px"
      pb="14px"
      borderRadius="14px"
      bg={isActive ? 'white' : 'whiteAlpha.600'}
      border="1px solid"
      borderColor={isActive ? 'transparent' : 'whiteAlpha.900'}
      boxShadow={
        isActive
          ? '0 14px 28px -12px rgba(0,0,0,0.35), 0 0 0 3px rgba(255,255,255,0.55)'
          : 'none'
      }
      color={isActive ? 'inkBlue' : 'white'}
      animation={isActive ? `${wobble} 3.2s ease-in-out infinite` : undefined}
    >
      <Flex align="center" justify="space-between">
        <StepBadge status={status} n={index + 1} />
        <Text
          fontFamily="heading"
          fontSize="11px"
          letterSpacing="0.6px"
          color="inkBlue"
          textTransform="uppercase"
          fontWeight="600"
        >
          {isActive ? 'Up next' : isDone ? 'Done' : `Step ${index + 1}`}
        </Text>
      </Flex>

      <Box flex="1" minW={0}>
        <Text
          fontFamily="heading"
          fontSize="16px"
          fontWeight="700"
          lineHeight="1.25"
          color="inkBlue"
          textDecorationColor="inkBlue"
          textDecoration={isDone ? 'line-through' : 'none'}
          mb={1}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {step.title}
        </Text>
        <Text
          fontFamily="body"
          fontSize="13px"
          lineHeight="1.4"
          color="inkBlue"
          opacity={isDone ? 0.9 : 1}
          noOfLines={2}
        >
          {step.blurb}
        </Text>
      </Box>

      {isActive ? (
        <Button
          onClick={onStart}
          variant="gradient"
          size="sm"
          rightIcon={<OnboardingArrowIcon boxSize="15px" />}
        >
          Start now
        </Button>
      ) : (
        <Text
          fontFamily="heading"
          fontSize="12px"
          letterSpacing="0.4px"
          color="blackAlpha.700"
        >
          {isDone ? 'Complete' : step.eta}
        </Text>
      )}
    </Flex>
  );
};

const ProgressBar = ({ done, total }: { done: number; total: number }) => {
  const pct = (done / total) * 100;
  return (
    <Box
      flex="1"
      h="8px"
      borderRadius="full"
      bg="whiteAlpha.400"
      overflow="hidden"
      position="relative"
    >
      <Box
        position="absolute"
        inset={0}
        right={`${100 - pct}%`}
        borderRadius="full"
        transition="right 0.45s cubic-bezier(.4,.7,.2,1)"
        bg="white"
      />
    </Box>
  );
};

const ChecklistHeader = ({
  doneCount,
  total,
}: {
  doneCount: number;
  total: number;
}) => (
  <Flex align="center" gap={16} mb={6}>
    <Flex align="center" gap={3} minW={0}>
      <Flex
        align="center"
        justify="center"
        boxSize="36px"
        borderRadius="10px"
        bg="whiteAlpha.200"
        border="1px solid"
        borderColor="whiteAlpha.600"
        backdropFilter="blur(4px)"
      >
        <Text
          fontFamily="heading"
          fontWeight="800"
          fontSize="14px"
          color="white"
        >
          {doneCount}
          <Box as="span" color="whiteAlpha.800">
            /{total}
          </Box>
        </Text>
      </Flex>
      <Box minW={0}>
        <Text textStyle="heading5" fontWeight="600" color="white">
          Complete your trial setup in 4 steps.
        </Text>
      </Box>
    </Flex>

    <Flex flex="1" align="center" gap={3}>
      <ProgressBar done={doneCount} total={total} />
      <Text
        fontFamily="heading"
        fontWeight="600"
        color="white"
        whiteSpace="nowrap"
      >
        {Math.round((doneCount / total) * 100)}%
      </Text>
    </Flex>
  </Flex>
);

const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { createTemplateTo } = useCreateTemplateAction();
  const isReadOnly = useIsReadOnly();
  const meCtx = useContext(MeContext) as {
    state: {
      onboardingChecklistEnabled: boolean | null;
      onboardingCompleted: boolean | null;
    };
  };

  const flagOn = meCtx.state.onboardingChecklistEnabled;
  const alreadyComplete = meCtx.state.onboardingCompleted;

  const { data } = useGetOnboardingStateQuery(undefined, {
    skip: !flagOn || alreadyComplete,
    refetchOnMountOrArgChange: true,
  });

  const activeStep = data?.next_step;
  const { data: recentCreatedPasses } = useListPassesQuery(
    { perPage: 1, sortBy: 'createdAt', sortOrder: 'desc' },
    { skip: activeStep !== 'add_to_wallet' }
  );
  const { data: recentActivePasses } = useListPassesQuery(
    {
      perPage: 1,
      filters: { status: ['active'] },
      sortBy: 'addedAt',
      sortOrder: 'desc',
    },
    { skip: activeStep !== 'send_notification' }
  );

  const walletPassId = recentCreatedPasses?.data?.[0]?.id;
  const notificationPassId = recentActivePasses?.data?.[0]?.id;

  const hrefForStep = (step: StepConfig): string => {
    if (step.nextStepName === 'add_to_wallet' && walletPassId) {
      return `/passes/${walletPassId}?highlight=wallet`;
    }
    if (step.nextStepName === 'send_notification' && notificationPassId) {
      return `/passes/${notificationPassId}?highlight=notification`;
    }
    return step.href;
  };

  if (!flagOn) return null;
  if (isReadOnly) return null;
  if (alreadyComplete) return null;
  if (!data) return null;
  if (data.completed) return null;

  const doneCount = STEPS.reduce(
    (acc, step) => acc + (data.steps[step.key] ? 1 : 0),
    0
  );

  return (
    <Box mb={8}>
      <Box
        position="relative"
        borderRadius="20px"
        p="2px"
        boxShadow="0 18px 48px -16px rgba(232,93,58,0.55), 0 4px 14px -4px rgba(242,78,126,0.35)"
      >
        <Box
          position="relative"
          borderRadius="18px"
          px="22px"
          py={5}
          color="white"
          overflow="hidden"
          background="onboardingBgGradient"
        >
          <ChecklistHeader doneCount={doneCount} total={STEPS.length} />
          <Grid
            templateColumns={{
              base: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              xl: 'repeat(4, 1fr)',
            }}
            gap={2.5}
          >
            {STEPS.map((step, i) => (
              <StepCard
                key={step.key}
                step={step}
                index={i}
                status={statusFor(step, data)}
                onStart={() =>
                  step.nextStepName === 'create_template'
                    ? navigate(createTemplateTo)
                    : navigate(hrefForStep(step))
                }
              />
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingChecklist;
